/**
 * @author       calvin@zumper.com (Calvin Yau)
 * @copyright    Copyright (c) 2023, Zumper
 * @description  Location scores thunk
 */

import { receiveLocationScores } from '@zcode-ui/poi'
import { selectCurrentSearch } from '@zcode-ui/web-zumper-global-state'
import { is404Error } from '@zcode/error-filters'
import { definitelyError } from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import type { Dispatch, GetState } from '@zcode/state-type'
import camelcaseKeys from 'camelcase-keys'
import { logger } from '../../../logger'

interface LocationScoresType {
  lat?: number | null
  lng?: number | null
}

const HOOD = 'hood'
const CITY = 'city'
export const HOOD_GEOGRAPHY_LEVEL = 10
export const CITY_GEOGRAPHY_LEVEL = 30

export const loadLocationScores =
  ({ lat, lng }: LocationScoresType) =>
  async (dispatch: Dispatch, getState: GetState) => {
    let hasScores = false
    const state = getState()
    const { city, hood } = selectCurrentSearch(state)

    let type = ''
    let name = ''
    let geographyLevel = CITY_GEOGRAPHY_LEVEL
    if (hood?.name) {
      name = hood.name
      type = HOOD
      geographyLevel = HOOD_GEOGRAPHY_LEVEL
    } else if (city?.name) {
      name = city.name
      type = CITY
    }

    if (lat && lng && type && name && geographyLevel) {
      const params = {
        lat,
        lng,
        geography_level: geographyLevel,
        type,
        name,
      }
      try {
        const res = await Api.locationScoresGeo.query(params)
        const locationScores = res.geographies?.[0]?.scores
        if (locationScores) {
          dispatch(receiveLocationScores(camelcaseKeys(locationScores, { deep: true })))
          hasScores = true
        }
      } catch (e) {
        if (!is404Error(e)) {
          const msg = `Failed to get location scores for LTR current search view, lat: ${lat}, lng: ${lng}, geography_level: ${geographyLevel}, type: ${type}, name: ${name}`
          logger.error(msg, definitelyError(msg, e))
        }
      }
    } else {
      logger.info('not enough info to make location scores geo call', {
        geographyLevel,
        lat,
        lng,
        name,
        type,
      })
    }
    if (!hasScores) {
      dispatch(receiveLocationScores({}))
    }
  }
